import { useLocalStorage, useNow } from '@vueuse/core'
import { DateTime } from 'luxon'
import { computed } from 'vue'
import { useGetMe } from '@/composables/get-me'
import { useTranslation } from '@/composables/use-i18n'

const STORAGE_KEY = 'power-miles-burn-reminder-dismissed-at'

export const usePowerMilesBurnReminder = () => {
  const { t } = useTranslation()
  const now = useNow({ interval: 60_000 })
  const dismissedByAccount = useLocalStorage<Record<string, string>>(STORAGE_KEY, {})
  const { accountAddress, isConnected } = useGetMe()

  const todayKey = computed(() => {
    return DateTime.fromJSDate(now.value).toISODate() ?? ''
  })

  const daysUntilBurn = computed(() => {
    const current = DateTime.fromJSDate(now.value)
    const burnDate = current.endOf('month').startOf('day')
    return Math.max(0, Math.round(burnDate.diff(current.startOf('day'), 'days').days))
  })

  const burnTitle = computed(() => {
    if (daysUntilBurn.value === 0) return t('powerMilesBurnReminder.titleToday')
    if (daysUntilBurn.value === 1) return t('powerMilesBurnReminder.titleTomorrow')
    return t('powerMilesBurnReminder.titleDays', { days: daysUntilBurn.value })
  })

  const isInReminderWindow = computed(() => daysUntilBurn.value <= 10)

  const isDismissedToday = computed(() => {
    if (!accountAddress.value) return false
    return dismissedByAccount.value[accountAddress.value] === todayKey.value
  })

  const shouldShowModal = computed(() => {
    return Boolean(
      isConnected.value &&
      isInReminderWindow.value &&
      !isDismissedToday.value,
    )
  })

  const dismissForToday = () => {
    if (!accountAddress.value || !todayKey.value) return
    dismissedByAccount.value = {
      ...dismissedByAccount.value,
      [accountAddress.value]: todayKey.value,
    }
  }

  return {
    burnTitle,
    daysUntilBurn,
    dismissForToday,
    isDismissedToday,
    isInReminderWindow,
    shouldShowModal,
  }
}
